import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Mail, Lock, Save, Loader2, Shield, Building2, Brain, Eye, EyeOff, Trash2, LogOut, BarChart3, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { ProfileAnalyticsDashboard } from '../components/AnalyticsDashboard';
import authAxios, { API } from '../utils/authAxios';

export function UserProfilePage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('account');
  const [userData, setUserData] = useState(null);
  const [userCenters, setUserCenters] = useState([]);
  const [centersAnalytics, setCentersAnalytics] = useState({});
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [passwordData, setPasswordData] = useState({ current: '', new_password: '', confirm: '' });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(function() {
    loadUserData();
  }, []);

  async function loadUserData() {
    try {
      const [userRes, centersRes] = await Promise.all([
        authAxios.get(API + '/user/profile', { withCredentials: true }),
        authAxios.get(API + '/user/centers', { withCredentials: true }).catch(() => ({ data: { centers: [] } }))
      ]);
      setUserData(userRes.data);
      setFormData({ name: userRes.data.name || '', email: userRes.data.email || '' });
      setUserCenters(centersRes.data.centers || []);
      
      // Load analytics for each center
      if (centersRes.data.centers && centersRes.data.centers.length > 0) {
        const analyticsPromises = centersRes.data.centers.map(center =>
          authAxios.get(API + '/center/' + center.center_id + '/analytics', { withCredentials: true })
            .then(res => ({ [center.center_id]: res.data }))
            .catch(() => ({ [center.center_id]: null }))
        );
        const analyticsResults = await Promise.all(analyticsPromises);
        const analyticsMap = analyticsResults.reduce((acc, item) => ({ ...acc, ...item }), {});
        setCentersAnalytics(analyticsMap);
      }
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) {
        navigate('/login');
      }
    }
    setLoading(false);
  }

  async function handleSaveProfile(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await authAxios.put(API + '/user/profile', formData, { withCredentials: true });
      toast.success('Perfil actualizado');
      loadUserData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar');
    }
    setSaving(false);
  }

  async function handleChangePassword(e) {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    setSaving(true);
    try {
      await authAxios.put(API + '/user/password', {
        current_password: passwordData.current,
        new_password: passwordData.new_password
      }, { withCredentials: true });
      toast.success('Contraseña actualizada');
      setPasswordData({ current: '', new_password: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cambiar contraseña');
    }
    setSaving(false);
  }

  async function handleLogout() {
    try {
      await authAxios.post(API + '/auth/logout', {}, { withCredentials: true });
      auth.logout();
      navigate('/');
    } catch (err) {
      console.error(err);
    }
  }

  function getRoleBadge(role) {
    const badges = {
      admin: { icon: Shield, label: 'Administrador', color: 'bg-red-500/10 text-red-400' },
      center: { icon: Building2, label: 'Centro', color: 'bg-emerald-500/10 text-emerald-400' },
      professional: { icon: Brain, label: 'Profesional', color: 'bg-blue-500/10 text-blue-400' },
      user: { icon: User, label: 'Usuario', color: 'bg-slate-500/10 text-slate-400' }
    };
    const badge = badges[role] || badges.user;
    const Icon = badge.icon;
    return (
      <span className={'flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ' + badge.color}>
        <Icon className="w-4 h-4" />{badge.label}
      </span>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Header />
        <div className="flex justify-center py-40"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          {/* Profile Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              {userData?.picture ? (
                <img src={userData.picture} alt={userData.name} className="w-16 h-16 rounded-full object-cover ring-2 ring-slate-700" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center">
                  <User className="w-8 h-8 text-slate-500" />
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold text-slate-50">{userData?.name || 'Mi Perfil'}</h1>
                <p className="text-slate-400">{userData?.email}</p>
              </div>
            </div>
            {getRoleBadge(userData?.role)}
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b border-slate-800 pb-4 overflow-x-auto">
            <Button variant={activeTab === 'account' ? 'default' : 'ghost'} onClick={() => setActiveTab('account')} className={activeTab === 'account' ? 'bg-emerald-500' : 'text-slate-400 hover:text-slate-50'}>
              <User className="w-4 h-4 mr-2" />Cuenta
            </Button>
            <Button variant={activeTab === 'security' ? 'default' : 'ghost'} onClick={() => setActiveTab('security')} className={activeTab === 'security' ? 'bg-emerald-500' : 'text-slate-400 hover:text-slate-50'}>
              <Lock className="w-4 h-4 mr-2" />Seguridad
            </Button>
            {(userData?.role === 'center' || userCenters.length > 0) && (
              <Button variant={activeTab === 'centers' ? 'default' : 'ghost'} onClick={() => setActiveTab('centers')} className={activeTab === 'centers' ? 'bg-emerald-500' : 'text-slate-400 hover:text-slate-50'}>
                <Building2 className="w-4 h-4 mr-2" />Mis Centros ({userCenters.length})
              </Button>
            )}
          </div>

          {/* Account Tab */}
          {activeTab === 'account' && (
            <div className="space-y-6">
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-slate-50">Información de la cuenta</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSaveProfile} className="space-y-4">
                    <div>
                      <Label className="text-slate-300">Nombre</Label>
                      <Input 
                        value={formData.name} 
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
                        className="bg-slate-950 border-slate-700 text-slate-200"
                        data-testid="profile-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Email</Label>
                      <Input 
                        type="email"
                        value={formData.email} 
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })} 
                        className="bg-slate-950 border-slate-700 text-slate-200"
                        disabled={userData?.auth_type === 'google'}
                        data-testid="profile-email-input"
                      />
                      {userData?.auth_type === 'google' && (
                        <p className="text-sm text-slate-500 mt-1">El email no se puede cambiar para cuentas de Google</p>
                      )}
                    </div>
                    <div className="pt-4">
                      <Button type="submit" disabled={saving} className="bg-emerald-500 hover:bg-emerald-600" data-testid="save-profile-btn">
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        Guardar cambios
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>

              {/* Account Info */}
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-slate-50">Detalles de la cuenta</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-slate-800">
                    <span className="text-slate-400">Tipo de cuenta</span>
                    <span className="text-slate-200">{userData?.auth_type === 'google' ? 'Google' : 'Email'}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-slate-800">
                    <span className="text-slate-400">Rol</span>
                    {getRoleBadge(userData?.role)}
                  </div>
                  <div className="flex justify-between items-center py-3">
                    <span className="text-slate-400">Miembro desde</span>
                    <span className="text-slate-200">{userData?.created_at ? new Date(userData.created_at).toLocaleDateString('es-CL') : '-'}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Logout */}
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-6">
                  <Button variant="outline" onClick={handleLogout} className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />Cerrar sesión
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="space-y-6">
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-slate-50">Cambiar contraseña</CardTitle>
                </CardHeader>
                <CardContent>
                  {userData?.auth_type === 'google' ? (
                    <div className="text-center py-8">
                      <Shield className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                      <p className="text-slate-400">Tu cuenta usa autenticación de Google</p>
                      <p className="text-sm text-slate-500 mt-2">La contraseña se gestiona desde tu cuenta de Google</p>
                    </div>
                  ) : (
                    <form onSubmit={handleChangePassword} className="space-y-4">
                      <div>
                        <Label className="text-slate-300">Contraseña actual</Label>
                        <div className="relative">
                          <Input 
                            type={showPassword ? 'text' : 'password'}
                            value={passwordData.current}
                            onChange={(e) => setPasswordData({ ...passwordData, current: e.target.value })}
                            className="bg-slate-950 border-slate-700 text-slate-200 pr-10"
                            data-testid="current-password-input"
                          />
                          <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <Label className="text-slate-300">Nueva contraseña</Label>
                        <Input 
                          type={showPassword ? 'text' : 'password'}
                          value={passwordData.new_password}
                          onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                          className="bg-slate-950 border-slate-700 text-slate-200"
                          data-testid="new-password-input"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Confirmar nueva contraseña</Label>
                        <Input 
                          type={showPassword ? 'text' : 'password'}
                          value={passwordData.confirm}
                          onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })}
                          className="bg-slate-950 border-slate-700 text-slate-200"
                          data-testid="confirm-password-input"
                        />
                      </div>
                      <div className="pt-4">
                        <Button type="submit" disabled={saving} className="bg-emerald-500 hover:bg-emerald-600" data-testid="change-password-btn">
                          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                          Cambiar contraseña
                        </Button>
                      </div>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Centers Tab */}
          {activeTab === 'centers' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-slate-50">Mis Centros de Rehabilitación</h2>
                <Button onClick={() => navigate('/center/register')} className="bg-emerald-500 hover:bg-emerald-600">
                  <Plus className="w-4 h-4 mr-2" />Agregar centro
                </Button>
              </div>

              {userCenters.length === 0 ? (
                <Card className="bg-slate-900 border-slate-800">
                  <CardContent className="py-12 text-center">
                    <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400 mb-4">No tienes centros registrados</p>
                    <Button onClick={() => navigate('/center/register')} className="bg-emerald-500 hover:bg-emerald-600">
                      <Plus className="w-4 h-4 mr-2" />Registrar mi primer centro
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                userCenters.map(center => (
                  <Card key={center.center_id} className="bg-slate-900 border-slate-800">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {center.logo ? (
                            <img src={center.logo} alt={center.name} className="w-14 h-14 rounded-lg object-cover" />
                          ) : (
                            <div className="w-14 h-14 rounded-lg bg-slate-800 flex items-center justify-center">
                              <Building2 className="w-6 h-6 text-slate-500" />
                            </div>
                          )}
                          <div>
                            <CardTitle className="text-slate-50 flex items-center gap-2">
                              {center.name}
                              {center.is_verified && (
                                <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-400 text-xs rounded">Verificado</span>
                              )}
                            </CardTitle>
                            <p className="text-sm text-slate-400">{center.city}</p>
                          </div>
                        </div>
                        <Button variant="outline" onClick={() => navigate('/center/dashboard?id=' + center.center_id)} className="border-slate-700 text-slate-300 hover:bg-slate-800">
                          Administrar
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {centersAnalytics[center.center_id] ? (
                        <div className="grid grid-cols-4 gap-4">
                          <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                            <p className="text-2xl font-bold text-slate-50">{centersAnalytics[center.center_id].views?.total || 0}</p>
                            <p className="text-sm text-slate-400">Visitas totales</p>
                          </div>
                          <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                            <p className="text-2xl font-bold text-slate-50">{centersAnalytics[center.center_id].views?.month || 0}</p>
                            <p className="text-sm text-slate-400">Este mes</p>
                          </div>
                          <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                            <p className="text-2xl font-bold text-slate-50">{centersAnalytics[center.center_id].clicks?.total || 0}</p>
                            <p className="text-sm text-slate-400">Clics</p>
                          </div>
                          <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                            <p className="text-2xl font-bold text-slate-50">{centersAnalytics[center.center_id].contacts?.total || 0}</p>
                            <p className="text-sm text-slate-400">Contactos</p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-slate-500 text-center py-4">Cargando estadísticas...</p>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
